import type { ReactNode } from 'react'
import { CircleHelp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SHELL_COLUMN_FOOTER_CLASS } from '@/lib/shell-chrome'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  LIST_GESTURE_TIPS,
  LIST_GESTURE_TIPS_WITH_PREVIEW,
} from '@/lib/list-gesture-tips'

export type ListSurfaceStatusBarProps = {
  /** Tip body shown from the (?) control. Defaults to standard list gestures. */
  tips?: string
  /** When true, appends PanelRight pin language (Home / Inbox / Search). */
  includePreviewTip?: boolean
  /** Optional left-side meta (counts, links). (?) stays bottom-right. */
  trailing?: ReactNode
  /** Optional leading slot on the left (before meta). */
  leading?: ReactNode
  className?: string
  'data-testid'?: string
  helpTestId?: string
  /** Count / query stats on the left. */
  metaTestId?: string
}

/**
 * Main-panel / list-column bottom status bar - same chrome language as
 * DocumentContentStatusBar (hairline top border, muted/30 wash, compact height).
 * Gesture tips live behind a bottom-right (?) that uses the same tooltip as the
 * document status (i). A native title attribute does not show on click.
 */
export function ListSurfaceStatusBar({
  tips,
  includePreviewTip = false,
  trailing,
  leading,
  className,
  'data-testid': dataTestId,
  helpTestId,
  metaTestId,
}: ListSurfaceStatusBarProps) {
  const tipText =
    tips ?? (includePreviewTip ? LIST_GESTURE_TIPS_WITH_PREVIEW : LIST_GESTURE_TIPS)

  return (
    <div
      className={cn(
        SHELL_COLUMN_FOOTER_CLASS,
        'flex items-center justify-between gap-2 px-2 border-t border-border bg-muted/30 text-xs min-w-0 shrink-0',
        className,
      )}
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        {leading}
        {trailing != null ? (
          <div
            className="flex items-center gap-2 min-w-0 text-muted-foreground"
            data-testid={metaTestId}
          >
            {trailing}
          </div>
        ) : null}
      </div>

      <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="List interaction tips"
            data-testid={helpTestId}
            className={cn(
              'flex items-center justify-center h-6 w-6 rounded shrink-0',
              'text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors',
            )}
          >
            <CircleHelp className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-sm text-xs">
          {tipText}
        </TooltipContent>
      </Tooltip>
      </TooltipProvider>
    </div>
  )
}
